/**
 * Admin Riders — supports two providers controlled by the Super Admin:
 *
 *   IN_APP  – tenant-onboarded riders (Add / Approve / Reject / Delete)
 *   BIZILGO – external pool synced from bizilgo.com (Block / Unblock only)
 *   BOTH    – tabbed view of both
 *
 * The mode + counts come from GET /api/admin/riders so the UI auto-adjusts.
 */
import { useEffect, useMemo, useState } from "react";
import {
    Bike, Plus, Loader2, CheckCircle2, XCircle, ShieldOff, ShieldCheck,
    Trash2, Clock4, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { Field, Modal } from "./AdminRestaurants";
import AdminSearchBar from "../../components/AdminSearchBar";
import { toast } from "sonner";

const EMPTY = { name: "", mobile_number: "", vehicle_type: "BIKE", vehicle_number: "" };

const STATE_META = {
    APPROVED: { Icon: CheckCircle2, label: "Approved", classes: "bg-emerald-100 text-emerald-700" },
    PENDING_APPROVAL: { Icon: Clock4, label: "Awaiting", classes: "bg-amber-100 text-amber-800" },
    REJECTED: { Icon: XCircle, label: "Rejected", classes: "bg-red-100 text-red-700" },
    NONE: { Icon: CheckCircle2, label: "Active", classes: "bg-muted text-muted-foreground" },
};

export default function AdminRiders() {
    const [data, setData] = useState({ rider_mode: "IN_APP", rows: [] });
    const [tab, setTab] = useState("IN_APP");
    const [query, setQuery] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [zoneAdmins, setZoneAdmins] = useState([]);

    const load = () =>
        api.get("/admin/riders")
            .then(({ data }) => {
                setData(data || { rider_mode: "IN_APP", rows: [] });
                // Default the tab to whichever side has rows so the page never
                // opens on an empty pane.
                if (data?.rider_mode === "BIZILGO") setTab("BIZILGO");
                else if (data?.rider_mode === "IN_APP") setTab("IN_APP");
            })
            .catch(() => setData({ rider_mode: "IN_APP", rows: [] }));
    useEffect(() => { load(); }, []);
    useEffect(() => {
        api.get("/admin/zone-admins")
            .then(({ data }) => setZoneAdmins(data || []))
            .catch(() => setZoneAdmins([]));
    }, []);

    const setZoneAdmin = async (r, value) => {
        try {
            await api.patch(`/admin/riders/${r.id}/zone-admin`, {
                zone_admin_user_id: value || null,
            });
            toast.success(value ? "Classified as zone driver" : "Unclassified");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const mode = data.rider_mode || "IN_APP";
    const canCreate = mode === "IN_APP" || mode === "BOTH";
    const showBoth = mode === "BOTH";

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filteredBySource = (data.rows || []).filter((r) => {
            if (!showBoth) return true;
            return r.source === tab;
        });
        if (!q) return filteredBySource;
        return filteredBySource.filter((r) => {
            const hay = `${r.name || ""} ${r.mobile_number || ""} ${r.vehicle_type || ""} ${r.approval_state || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [data, query, tab, showBoth]);

    const submit = async () => {
        if (!form.name.trim() || !form.mobile_number.trim()) return;
        setBusy(true);
        try {
            await api.post("/admin/riders", {
                name: form.name.trim(),
                mobile_number: form.mobile_number.trim(),
                vehicle_type: form.vehicle_type,
                vehicle_number: form.vehicle_number || null,
            });
            toast.success("Rider added — awaiting your approval");
            setModalOpen(false); setForm(EMPTY); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to create rider");
        } finally { setBusy(false); }
    };

    const approve = async (r) => {
        try { await api.post(`/admin/riders/${r.id}/approve`); toast.success("Approved"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to approve"); }
    };
    const reject = async (r) => {
        const reason = window.prompt("Reject this rider. Optional reason:", "");
        if (reason === null) return;
        try { await api.post(`/admin/riders/${r.id}/reject`, { reason }); toast.success("Rejected"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to reject"); }
    };
    const block = async (r) => {
        try { await api.post(`/admin/riders/${r.id}/block`); toast.success(`${r.name} blocked`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to block"); }
    };
    const unblock = async (r) => {
        try { await api.post(`/admin/riders/${r.id}/unblock`); toast.success(`${r.name} unblocked`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to unblock"); }
    };
    const remove = async (r) => {
        if (!window.confirm(`Delete rider ${r.name}? This cannot be undone.`)) return;
        try { await api.delete(`/admin/riders/${r.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    };

    return (
        <div data-testid="admin-riders">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Riders</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Provider: <strong>{mode === "BOTH" ? "Bizil + BizilGO" : mode === "BIZILGO" ? "BizilGO" : "Bizil in-app"}</strong>
                        {" · "}{data.in_app_count || 0} in-app · {data.bizilgo_count || 0} from BizilGO
                        {data.blocked_count ? ` · ${data.blocked_count} blocked` : ""}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <AdminSearchBar value={query} onChange={setQuery} placeholder="Search riders…" testid="riders-search" />
                    {canCreate && (
                        <button data-testid="add-rider" onClick={() => setModalOpen(true)}
                            className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2">
                            <Plus size={16} /> Add Rider
                        </button>
                    )}
                </div>
            </div>

            {showBoth && (
                <div className="mt-4 inline-flex gap-1 p-1 bg-muted rounded-xl">
                    {[
                        { key: "IN_APP", label: `Bizil riders · ${data.in_app_count || 0}` },
                        { key: "BIZILGO", label: `BizilGO pool · ${data.bizilgo_count || 0}` },
                    ].map((t) => (
                        <button key={t.key} data-testid={`riders-tab-${t.key}`} onClick={() => setTab(t.key)}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg ${tab === t.key ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
                            {t.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="mt-4 bg-card border border-border rounded-2xl overflow-hidden">
                {rows.length === 0 ? (
                    <div className="p-10 text-center">
                        <Bike className="mx-auto mb-3 text-muted-foreground" size={32} />
                        <p className="font-semibold">No riders here yet</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            {mode === "BIZILGO"
                                ? "Riders from BizilGO will appear once they sign up at bizilgo.com."
                                : "Tap “Add Rider” to onboard one. They'll appear here pending your approval."}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Mobile + tablet portrait (<lg): stacked card view — keeps all action buttons reachable without horizontal scroll. */}
                        <div className="lg:hidden">
                            {rows.map((r) => {
                                const meta = STATE_META[r.approval_state] || STATE_META.NONE;
                                return (
                                    <div key={`m-${r.source}-${r.id}`} data-testid={`rider-card-${r.id}`} className="border-t border-border first:border-t-0 px-4 py-3">
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="font-semibold text-sm inline-flex items-center gap-1.5 flex-wrap">
                                                    <span className="truncate">{r.name}</span>
                                                    {r.source === "BIZILGO" && (
                                                        <span className="text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                                                            <ExternalLink size={9} /> BizilGO
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{r.mobile_number}</div>
                                                <div className="text-[11px] text-muted-foreground">{r.vehicle_type}{r.vehicle_number ? ` · ${r.vehicle_number}` : ""}</div>
                                            </div>
                                            <span className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${meta.classes}`}>
                                                <meta.Icon size={11} /> {meta.label}
                                            </span>
                                        </div>
                                        <div className="mt-2 flex items-center flex-wrap gap-1">
                                            {r.blocked && (
                                                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-red-100 text-red-700 inline-flex items-center gap-1">
                                                    <ShieldOff size={10} /> Blocked
                                                </span>
                                            )}
                                            {r.status && <span className="text-[10px] font-semibold text-muted-foreground">{r.status}</span>}
                                        </div>
                                        {r.approval_state === "REJECTED" && r.rejection_reason && (
                                            <p className="text-[11px] text-red-700 mt-1">Reason: {r.rejection_reason}</p>
                                        )}
                                        {r.source === "IN_APP" && (
                                            <div className="mt-2">
                                                <select
                                                    data-testid={`rider-zone-admin-card-${r.id}`}
                                                    className="bg-muted rounded-lg px-2 py-1 text-xs font-semibold w-full"
                                                    value={r.zone_admin_user_id || ""}
                                                    onChange={(e) => setZoneAdmin(r, e.target.value)}
                                                >
                                                    <option value="">— tenant rider —</option>
                                                    {zoneAdmins.map((za) => (
                                                        <option key={za.id} value={za.id}>
                                                            {za.first_name} {za.last_name || ""}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}
                                        <div className="mt-3 flex justify-end">
                                            <div className="inline-flex items-center gap-1">
                                                {r.source === "IN_APP" && r.approval_state === "PENDING_APPROVAL" && (
                                                    <>
                                                        <button data-testid={`rider-approve-card-${r.id}`} onClick={() => approve(r)} className="touch-target rounded-lg text-emerald-600 hover:bg-emerald-50" title="Approve"><CheckCircle2 size={16} /></button>
                                                        <button data-testid={`rider-reject-card-${r.id}`} onClick={() => reject(r)} className="touch-target rounded-lg text-red-600 hover:bg-red-50" title="Reject"><XCircle size={16} /></button>
                                                    </>
                                                )}
                                                {r.source === "BIZILGO" && (
                                                    r.blocked ? (
                                                        <button data-testid={`rider-unblock-card-${r.id}`} onClick={() => unblock(r)} className="touch-target rounded-lg text-emerald-600 hover:bg-emerald-50" title="Unblock"><ShieldCheck size={16} /></button>
                                                    ) : (
                                                        <button data-testid={`rider-block-card-${r.id}`} onClick={() => block(r)} className="touch-target rounded-lg text-red-600 hover:bg-red-50" title="Block"><ShieldOff size={16} /></button>
                                                    )
                                                )}
                                                {r.source === "IN_APP" && (
                                                    <button data-testid={`rider-delete-card-${r.id}`} onClick={() => remove(r)} className="touch-target rounded-lg text-destructive hover:bg-destructive/10" title="Delete"><Trash2 size={16} /></button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Desktop (lg+): classic table with horizontal scroll if needed. */}
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="w-full text-sm min-w-[820px]">
                                <thead>
                                    <tr className="bg-muted/60">
                                        <Th>Name</Th><Th>Mobile</Th><Th>Vehicle</Th>
                                        <Th>Zone driver</Th>
                                        <Th>State</Th><Th>Status</Th>
                                        {/* iter146 — sticky so Approve/Reject/Block/Delete
                                            stay reachable without scrolling all the way
                                            right on narrower desktop/laptop widths. */}
                                        <Th className="text-right sticky right-0 bg-muted/60 border-l border-border">Actions</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => {
                                        const meta = STATE_META[r.approval_state] || STATE_META.NONE;
                                        return (
                                            <tr key={`${r.source}-${r.id}`} data-testid={`rider-row-${r.id}`}
                                                className="border-t border-border">
                                                <Td>
                                                    <div className="font-semibold inline-flex items-center gap-1.5">
                                                        {r.name}
                                                        {r.source === "BIZILGO" && (
                                                            <span className="text-[9px] font-bold uppercase tracking-wider bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                                                                <ExternalLink size={9} /> BizilGO
                                                            </span>
                                                        )}
                                                    </div>
                                                </Td>
                                                <Td>{r.mobile_number}</Td>
                                                <Td>{r.vehicle_type}{r.vehicle_number ? ` · ${r.vehicle_number}` : ""}</Td>
                                                <Td>
                                                    {r.source === "IN_APP" ? (
                                                        <select
                                                            data-testid={`rider-zone-admin-${r.id}`}
                                                            className="bg-muted rounded-lg px-2 py-1 text-xs font-semibold"
                                                            value={r.zone_admin_user_id || ""}
                                                            onChange={(e) => setZoneAdmin(r, e.target.value)}
                                                        >
                                                            <option value="">— tenant rider —</option>
                                                            {zoneAdmins.map((za) => (
                                                                <option key={za.id} value={za.id}>
                                                                    {za.first_name} {za.last_name || ""}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">—</span>
                                                    )}
                                                </Td>
                                                <Td>
                                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${meta.classes}`}>
                                                        <meta.Icon size={11} /> {meta.label}
                                                    </span>
                                                    {r.blocked && (
                                                        <span className="ml-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full bg-red-100 text-red-700 inline-flex items-center gap-1">
                                                            <ShieldOff size={10} /> Blocked
                                                        </span>
                                                    )}
                                                    {r.approval_state === "REJECTED" && r.rejection_reason && (
                                                        <p className="text-[11px] text-red-700 mt-1">Reason: {r.rejection_reason}</p>
                                                    )}
                                                </Td>
                                                <Td>{r.status || "—"}</Td>
                                                <Td className="text-right sticky right-0 bg-card border-l border-border">
                                                    <div className="inline-flex items-center gap-1">
                                                        {r.source === "IN_APP" && r.approval_state === "PENDING_APPROVAL" && (
                                                            <>
                                                                <button data-testid={`rider-approve-${r.id}`} onClick={() => approve(r)} className="touch-target rounded-lg text-emerald-600 hover:bg-emerald-50" title="Approve"><CheckCircle2 size={16} /></button>
                                                                <button data-testid={`rider-reject-${r.id}`} onClick={() => reject(r)} className="touch-target rounded-lg text-red-600 hover:bg-red-50" title="Reject"><XCircle size={16} /></button>
                                                            </>
                                                        )}
                                                        {r.source === "BIZILGO" && (
                                                            r.blocked ? (
                                                                <button data-testid={`rider-unblock-${r.id}`} onClick={() => unblock(r)} className="touch-target rounded-lg text-emerald-600 hover:bg-emerald-50" title="Unblock"><ShieldCheck size={16} /></button>
                                                            ) : (
                                                                <button data-testid={`rider-block-${r.id}`} onClick={() => block(r)} className="touch-target rounded-lg text-red-600 hover:bg-red-50" title="Block"><ShieldOff size={16} /></button>
                                                            )
                                                        )}
                                                        {r.source === "IN_APP" && (
                                                            <button data-testid={`rider-delete-${r.id}`} onClick={() => remove(r)} className="touch-target rounded-lg text-destructive hover:bg-destructive/10" title="Delete"><Trash2 size={16} /></button>
                                                        )}
                                                    </div>
                                                </Td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>

            {modalOpen && (
                <Modal title="Add new rider" onClose={() => setModalOpen(false)}>
                    <div className="grid grid-cols-2 gap-3">
                        <Field full label="Name"><input data-testid="rider-form-name" autoFocus className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                        <Field label="Mobile"><input data-testid="rider-form-mobile" className="inp" value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} placeholder="10-digit" /></Field>
                        <Field label="Vehicle">
                            <select data-testid="rider-form-vehicle" className="inp" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}>
                                <option value="BIKE">Bike</option>
                                <option value="SCOOTER">Scooter</option>
                                <option value="BICYCLE">Bicycle</option>
                                <option value="CAR">Car</option>
                            </select>
                        </Field>
                        <Field full label="Vehicle number (optional)"><input className="inp" value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} placeholder="TS09AB1234" /></Field>
                    </div>
                    <p className="text-[11px] text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-2 mt-3">
                        The rider lands in <strong>Pending Approval</strong>. They can't sign in or accept orders until you approve them.
                    </p>
                    <button data-testid="rider-form-save" disabled={busy || !form.name.trim() || !form.mobile_number.trim()} onClick={submit}
                        className="mt-4 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                        {busy && <Loader2 size={16} className="animate-spin" />} Submit
                    </button>
                </Modal>
            )}
        </div>
    );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
